import { FC, memo, useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Grid, Typography, Icon } from "@mui/material";
import { ReactComponent as JobSummaryIcon } from "../../../../imagesV2/JobSummary.svg";

// Types
export interface JobSummaryProps {
  jobSummary: Record<string, string[]> | null;
  aiSummaryTitle: string;
  aiSummaryDescription: string;
}

// Component

export const JobSummary: FC<JobSummaryProps> = memo(
  ({ jobSummary, aiSummaryTitle, aiSummaryDescription }) => {
    const sections = useMemo(() => {
      if (!jobSummary) return [];
      return Object.entries(jobSummary)
        .map(([title, items]) => ({
          title,
          items: Array.isArray(items)
            ? items.filter((i): i is string => typeof i === "string")
            : [],
        }))
        .filter((section) => section.items.length > 0);
    }, [jobSummary]);

    return (
      <Box className="jobSummaryCard">
        <Box className="jobSummaryContent">
          {/* Header */}
          <div className="jobSummaryHeader">
            <Icon component={JobSummaryIcon} fontSize="small" title={aiSummaryTitle} />
            <Typography component="h2">{aiSummaryTitle}</Typography>
          </div>

          {/* Description */}
          <div className="jobSummaryHeader">
            <Typography component="p">{aiSummaryDescription}</Typography>
          </div>

          {/* Sections Grid */}
          <Grid container spacing={2}>
            {sections.map(({ title, items }) => (
              <Grid item key={title} xs={12} md={6} sm={12} lg={4} className="jobSummaryItems">
                <Typography component="h3">{title}</Typography>
                <ul>
                  {items.map((item) => (
                    <li key={item}>
                      <Typography component="span">{item}</Typography>
                    </li>
                  ))}
                </ul>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }
);

JobSummary.displayName = "JobSummary";

// runtime prop-type validation
JobSummary.propTypes = {
  jobSummary: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string.isRequired).isRequired)
    .isRequired,
  aiSummaryTitle: PropTypes.string.isRequired,
  aiSummaryDescription: PropTypes.string.isRequired,
};
